import moment from 'moment';
import HelperMixin from '../../Shared/HelperMixin.vue';
import PaginationHelper from '../../Shared/PaginationHelper.vue';

const MOVEMENT_CATEGORIES = [
    { value: '', label: 'كل الحركات' },
    { value: 'dispatch', label: 'صرف' },
    { value: 'inbound', label: 'ورد' },
    { value: 'damage', label: 'تالف' },
    { value: 'return', label: 'إرجاع' },
    { value: 'internal', label: 'صرف داخلي' },
];

export default {
    name: 'StockMovements',
    mixins: [HelperMixin],
    components: { PaginationHelper },

    async created() {
        await this.CheckLoginStatus();
        if (!this.loginDetails || !this.canViewPage) {
            this.$helper.ShowMessage('error', 'غير مصرح', 'ليس لديك صلاحية للوصول إلى حركات المخزون');
            this.href('/Home');
            return;
        }

        if (this.isFacilityCenterRole() && this.loginDetails.branchesId) {
            this.filters.facilityBranchesId = this.loginDetails.branchesId;
        }

        await this.loadLookups();
        await this.loadAll();
    },

    computed: {
        canViewPage() {
            return this.hasInventoryAndStockAccessUi()
                || this.isMinistryPharmacyDepartmentRole()
                || (this.isFacilityCenterRole() && this.loginDetails && this.loginDetails.branchesId);
        },
        canPrintPage() {
            if (!this.canViewPage) return false;
            if (this.isMinistryPharmacyDepartmentRole()) return false;
            return true;
        },
        movementCategories() {
            return MOVEMENT_CATEGORIES;
        },
        statsCards() {
            const s = this.statistics || {};
            return [
                this.statCard(
                    'إجمالي الحركات (آخر 30 يوماً)',
                    s.totalMovementsLast30,
                    'primary',
                    'ti ti-chart-arrows',
                    'حركات المخزون خلال 30 يوماً'
                ),
                this.statCard(
                    'إجمالي الكمية المصروفة',
                    s.dispatchedQty,
                    'danger',
                    'ti ti-arrow-down',
                    'صرف / صرف مباشر'
                ),
                this.statCard(
                    'إجمالي الكمية الواردة',
                    s.inboundQty,
                    'success',
                    'ti ti-arrow-up',
                    'توريد / استلام من مورد'
                ),
                this.statCard(
                    'قيمة الصرف',
                    s.dispatchValue,
                    'info',
                    'ti ti-coin',
                    'الكميات المصروفة × سعر الوحدة'
                ),
                this.statCard(
                    'قيمة التالف',
                    s.damageValue,
                    'warning',
                    'ti ti-alert-triangle',
                    'حركات نوع: تالف'
                ),
            ];
        },
        totalPages() {
            return Math.ceil((this.pages || 0) / (this.filters.pageSize || 25));
        },
        showFacilityFilter() {
            return !this.isFacilityCenterRole();
        },
    },

    filters: {
        moment(date) {
            if (!date) return '—';
            return moment(date).format('DD/MM/YYYY HH:mm');
        },
        momentDate(date) {
            if (!date) return '—';
            return moment(date).format('DD/MM/YYYY');
        },
    },

    data() {
        return {
            // pagination/filters
            filters: {
                pageNo: 1,
                pageSize: 25,
                facilityBranchesId: null,
                stockingId: null,
                movementCategory: '',
                from: null,
                to: null,
                search: '',
            },
            pages: 0,
            Info: [],
            statistics: {},
            branchesList: [],
            stockingList: [],
            // detail modal
            detailDialog: false,
            detailLoading: false,
            detailInfo: null,
            detailTrail: [],
            loadingList: false,
        };
    },

    methods: {
        tableRowNumber(index) {
            const pn = Number(this.filters.pageNo || 1);
            const ps = Number(this.filters.pageSize || 25);
            return (pn - 1) * ps + index + 1;
        },

        async loadLookups() {
            try {
                const br = await this.$http.GetAllBranches();
                let list = (br && br.data && br.data.info) || [];
                if (this.isFacilityCenterRole() && this.loginDetails && this.loginDetails.branchesId) {
                    list = list.filter(x => x.id === this.loginDetails.branchesId);
                }
                this.branchesList = list;
            } catch {
                this.branchesList = [];
            }

            try {
                const st = await this.$http.GetAllStocking();
                let list = (st && st.data && st.data.info) || [];
                if (this.isFacilityCenterRole() && this.loginDetails && this.loginDetails.branchesId) {
                    list = list.filter(x => x.branchesId === this.loginDetails.branchesId);
                }
                this.stockingList = list;
            } catch {
                this.stockingList = [];
            }
        },

        async loadAll() {
            await Promise.all([this.GetStatistics(), this.GetInfo(1)]);
        },

        GetStatistics() {
            return this.$http.GetStockMovementsStatistics(this.queryFilters(false))
                .then(res => { this.statistics = (res && res.data) || {}; })
                .catch(() => { this.statistics = {}; });
        },

        queryFilters(includePaging) {
            const q = {
                facilityBranchesId: this.filters.facilityBranchesId || null,
                stockingId: this.filters.stockingId || null,
                movementCategory: this.filters.movementCategory || null,
                search: (this.filters.search || '').trim() || null,
                from: this.filters.from || null,
                to: this.filters.to || null,
            };
            if (includePaging) {
                q.pageNo = this.filters.pageNo;
                q.pageSize = this.filters.pageSize;
            }
            return q;
        },

        GetInfo(pageNo) {
            this.filters.pageNo = pageNo || 1;
            this.loadingList = true;
            this.$blockUI.Start();
            this.$http.GetStockMovements(this.queryFilters(true))
                .then(res => {
                    this.$blockUI.Stop();
                    this.loadingList = false;
                    this.Info = (res && res.data && res.data.info) || [];
                    this.pages = (res && res.data && res.data.count) || 0;
                })
                .catch(() => {
                    this.$blockUI.Stop();
                    this.loadingList = false;
                    this.Info = [];
                    this.pages = 0;
                });
        },

        ApplyFilters() {
            this.GetInfo(1);
            this.GetStatistics();
        },

        ResetFilters() {
            this.filters.search = '';
            this.filters.movementCategory = '';
            this.filters.stockingId = null;
            this.filters.from = null;
            this.filters.to = null;
            if (!this.isFacilityCenterRole()) {
                this.filters.facilityBranchesId = null;
            }
            this.filters.pageSize = 25;
            this.ApplyFilters();
        },

        movementBadgeClass(category) {
            switch (category) {
                case 'صرف': return 'movement-badge movement-badge--dispatch';
                case 'ورد': return 'movement-badge movement-badge--inbound';
                case 'تالف': return 'movement-badge movement-badge--damage';
                case 'إرجاع': return 'movement-badge movement-badge--return';
                case 'صرف داخلي': return 'movement-badge movement-badge--internal';
                default: return 'movement-badge';
            }
        },

        qtyClass(row) {
            if (!row) return 'stock-qty';
            const q = Number(row.quantityChange || 0);
            if (q > 0) return 'stock-qty stock-qty--in';
            if (q < 0) return 'stock-qty stock-qty--out';
            return 'stock-qty';
        },

        qtyDisplay(row) {
            if (!row) return '0';
            const q = Number(row.quantityChange || 0);
            if (q > 0) return '+' + q;
            return String(q);
        },

        formatPrice(value) {
            const v = Number(value || 0);
            if (!isFinite(v)) return '0.00';
            return v.toFixed(2);
        },

        formatExpiry(value) {
            if (!value) return '—';
            return moment(value).format('DD/MM/YYYY');
        },

        openDetail(row) {
            if (!row || !row.id) return;
            this.detailDialog = true;
            this.detailLoading = true;
            this.detailInfo = null;
            this.detailTrail = [];
            this.$http.GetStockMovementById(row.id)
                .then(res => {
                    this.detailLoading = false;
                    this.detailInfo = (res && res.data && res.data.info) || null;
                    this.detailTrail = (res && res.data && res.data.trail) || [];
                })
                .catch(() => {
                    this.detailLoading = false;
                    this.$helper.ShowMessage('error', 'تعذّر التحميل', 'فشل جلب تفاصيل الحركة.');
                });
        },

        closeDetail() {
            this.detailDialog = false;
            this.detailInfo = null;
            this.detailTrail = [];
        },

        printMovement(row) {
            if (!row || !row.id) return;
            if (!this.canPrintPage) {
                this.$helper.ShowMessage('warning', 'غير مسموح', 'ليس لديك صلاحية طباعة سندات الحركة.');
                return;
            }
            const url = this.$http.GetStockMovementPrintUrl(row.id);
            window.open(url, '_blank');
        },

        ExportExcel() {
            const url = this.$http.GetStockMovementsExportExcelUrl(this.queryFilters(false));
            window.open(url, '_blank');
        },

        ExportPdf() {
            const url = this.$http.GetStockMovementsExportPdfUrl(this.queryFilters(false));
            window.open(url, '_blank');
        },
    },
};
